import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import {
  fetchPermissionHistoryFailure,
  fetchPermissionHistorySuccess,
  fetchPermissionsOverviewFailure,
  fetchPermissionsOverviewSuccess,
} from "./actions";
import {
  FETCH_PERMISSIONS_OVERVIEW,
  FETCH_PERMISSION_HISTORY,
} from "./constants";

/** `utils/api` already unwraps axios, so `data` is the API's own envelope. */
const unwrap = (data) => {
  const body = data?.data ?? data ?? {};
  return body && typeof body === "object" && !Array.isArray(body) ? body : {};
};

export function* fetchPermissionsOverviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const locationId = payload?.locationId ?? null;
  const params = [];
  if (locationId !== null && locationId !== undefined) {
    params.push(`location_id=${encodeURIComponent(locationId)}`);
  }
  // Frontline staff are opt-in on the API and always wanted here: the People lens shows
  // them as a collapsed band of promotion candidates. Without the flag the endpoint
  // returns managers and administrators only.
  if (payload?.includeEmployees !== false) {
    params.push("include_employees=1");
  }
  const query = params.length > 0 ? `?${params.join("&")}` : "";

  try {
    const { data } = yield call(
      api(token).get,
      `/api/v2/employee/permissions-overview${query}`,
    );
    const body = unwrap(data);
    const result = {
      employees: Array.isArray(body?.employees) ? body.employees : [],
      locations: Array.isArray(body?.locations) ? body.locations : [],
      fetchedAt: new Date().toISOString(),
    };
    yield put(fetchPermissionsOverviewSuccess(result));
    if (onSuccess) onSuccess(result);
  } catch (e) {
    // 403 is the endpoint's answer to "you may not manage permissions" — a
    // legitimate reply the screen renders as NoPermission, never as a red toast.
    const forbidden = e?.response?.status === 403;
    yield put(fetchPermissionsOverviewFailure({ forbidden }));
    if (forbidden) return;
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while loading permissions."));
  }
}

/**
 * One employee's permission-change history.
 *
 * Deliberately quiet on failure: history is reference material shown at the bottom of an
 * editor, and a red toast over a panel the person opened to change somebody's access
 * would read as "the save failed". The drawer renders the absence instead.
 */
export function* fetchPermissionHistorySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const uuid = payload?.uuid ?? null;

  if (!uuid) {
    yield put(fetchPermissionHistoryFailure({ uuid }));
    return;
  }

  try {
    const { data } = yield call(
      api(token).get,
      `/api/v2/employee/permission-history/${encodeURIComponent(uuid)}`,
    );
    const body = unwrap(data);
    const result = {
      uuid,
      // Only an explicit `false` means "the audit table is not there yet" — an older API
      // that does not send the key at all is treated as available and simply returns none.
      available: body?.available !== false,
      entries: Array.isArray(body?.entries) ? body.entries : [],
    };
    yield put(fetchPermissionHistorySuccess(result));
    if (onSuccess) onSuccess(result);
  } catch (e) {
    yield put(fetchPermissionHistoryFailure({ uuid }));
  }
}

export function* fetchPermissionsOverviewFlow() {
  yield takeLatest(FETCH_PERMISSIONS_OVERVIEW, fetchPermissionsOverviewSaga);
}

export function* fetchPermissionHistoryFlow() {
  yield takeLatest(FETCH_PERMISSION_HISTORY, fetchPermissionHistorySaga);
}

export default function* EmployeePermissionsSaga() {
  yield all([fetchPermissionsOverviewFlow(), fetchPermissionHistoryFlow()]);
}
